'use client';

import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { VIDEO_GENERATION_FORM_DEFAULTS } from '@app/(darkHeader)/ai/_shared/_context/VideoGenerationFormState';
import { failureReasonContent, IMAGE_GENERATION_MODEL_OPTIONS } from '@app/(darkHeader)/ai/_shared/_context/constants';
import { canRetry } from '@app/(darkHeader)/ai/_shared/_context/failureReason';
import { AI_TOOLKIT_CATEGORIES } from '@app/(darkHeader)/ai/_shared/_context/types';
import { AiToolkitMainPanel } from '@app/(darkHeader)/ai/_shared/_layouts/AiToolkitMainPanel';
import { GenerationFlowErrorLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowErrorLayout';
import { GenerationFlowInProgressLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowInProgressLayout';
import { GenerationFlowSuccessLayout } from '@app/(darkHeader)/ai/_shared/_layouts/GenerationFlow/GenerationFlowSuccessLayout';
import { PostGenerationActionSection } from '@app/(darkHeader)/ai/_shared/_layouts/PostGenerationActionSection';
import { urlToFile } from '@app/(darkHeader)/ai/_shared/_utils/urlToFile';
import sendAiContentDownloadEvent from 'components/components/events/AiContentDownloadEvent';
import { telemetryIncrementAction } from 'core-events/actions';
import { useRouter } from 'next/navigation';
import { useCallback, type JSX } from 'react';

import { Text } from '@videoblocks/design-system';
import { GenerateImage, Image } from '@videoblocks/design-system/icons';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { useImageGenerationForm } from '../useImageGenerationForm';
import { AiToolkitImagePreview } from './AIToolkitImagePreview';

export const ImageGenerationMainPanel = (): JSX.Element | null => {
    const { submitStates, formStates, updateFormState } = useAiToolkitContext();
    const router = useRouter();
    const {
        status,
        imageUrl,
        aiContentId,
        lastAiContentId,
        aiContentType: submittedAiContentType,
        aiModelName: submittedAiModelName,
        failureReason,
    } = submitStates[AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION];
    const { onConfirm, isValid, formState } = useImageGenerationForm();
    const { referenceImages } = formState;
    const isLoading = status === 'inProgress';
    const isSuccess = status === 'success';
    const isError = status === 'error';

    const usesReferenceImages = referenceImages.length > 0;
    const eventAiContentType =
        submittedAiContentType ?? (usesReferenceImages ? AiContentType.IMAGE_TO_IMAGE : AiContentType.TEXT_TO_IMAGE);
    const eventAiModelName = submittedAiModelName ?? IMAGE_GENERATION_MODEL_OPTIONS[0].value;

    const handleDownload = useCallback(() => {
        if (!imageUrl) return;
        if (typeof window !== 'undefined') {
            void sendAiContentDownloadEvent({
                aiContentId,
                voiceId: null,
                aiContentVoiceoverType: null,
                currentUrl: window.location.href,
                referrer: document.referrer || undefined,
                lastAiContentId,
                aiModelName: eventAiModelName,
                aiContentType: eventAiContentType,
            });
        }
    }, [imageUrl, aiContentId, lastAiContentId, eventAiContentType, eventAiModelName]);

    const handleGenerateAsVideo = useCallback(async (): Promise<void> => {
        if (!imageUrl) return;
        const file = await urlToFile(imageUrl, 'generated-image.png');
        const currentVideoFormState =
            formStates[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION] ?? VIDEO_GENERATION_FORM_DEFAULTS;
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, {
            ...currentVideoFormState,
            file,
            selectedLibraryImage: null,
        });
        void telemetryIncrementAction('ai.image-generation.generate-as-video.clicked');
        router.push('/ai/video-generation');
    }, [imageUrl, formStates, updateFormState, router]);

    const idleView = (
        <div className="size-full flex flex-col items-center justify-center gap-3">
            <div className="size-16 flex items-center justify-center rounded-lg p-4 dark:bg-neutral-dark-200">
                <Image width={32} height={32} aria-hidden className="dark:[&_path]:fill-neutral-dark-700" />
            </div>
            <div className="flex flex-col gap-2 items-center text-center max-w-82.5 w-full">
                <Text>Image Generator</Text>
                <Text fontSize="sm" fontWeight="regular" className="text-neutral-dark-700">
                    Describe the image you want to generate. Add reference images for more control.
                </Text>
            </div>
        </div>
    );

    const errorContent = failureReason ? failureReasonContent(failureReason) : undefined;
    const errorView = (
        <GenerationFlowErrorLayout
            title={errorContent?.title}
            subtitle={errorContent?.subtitle}
            canRetry={canRetry(failureReason)}
            onConfirm={() => void onConfirm()}
            isValid={isValid}
        />
    );
    const inProgressView = <GenerationFlowInProgressLayout title="Your image is generating..." />;
    const successView = (
        <GenerationFlowSuccessLayout title="Preview and download your generated image below">
            <AiToolkitImagePreview alt="Generated image preview" src={imageUrl ?? ''} />
            <PostGenerationActionSection
                downloadUrl={imageUrl ?? ''}
                downloadFilename="generated-image.png"
                onDownload={handleDownload}
                category={AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION}
                aiContentId={aiContentId}
                lastAiContentId={lastAiContentId}
                aiContentType={eventAiContentType}
                aiModelName={eventAiModelName}
                secondaryButtonText="Generate as Video"
                onSecondaryButtonClick={() => void handleGenerateAsVideo()}
            />
        </GenerationFlowSuccessLayout>
    );

    return (
        <AiToolkitMainPanel heading="Image Generator" icon={<GenerateImage width={16} height={16} />}>
            {isError ? errorView : isLoading ? inProgressView : isSuccess ? successView : idleView}
        </AiToolkitMainPanel>
    );
};
